/**
 * Maturity Model Validation Schemas
 */

import { z } from 'zod';
import { optionalBoundedText } from './shared';

export const createMaturityFrameworkSchema = z.object({
  name: z
    .string()
    .min(1, 'Framework name is required')
    .max(200, 'Framework name must be less than 200 characters'),
  description: optionalBoundedText(2000, 'Description must be less than 2000 characters'),
  version: z
    .string()
    .min(1, 'Version is required')
    .regex(/^\d+\.\d+$/, 'Version must be in format N.N (e.g. 1.0)'),
  status: z.enum(['Active', 'Draft', 'Deprecated']).default('Draft'),
  effectiveFrom: z.string().min(1, 'Effective from date is required'),
  effectiveTo: z.string().optional(),
});

export const updateMaturityFrameworkSchema = createMaturityFrameworkSchema
  .partial()
  .refine((data) => Object.values(data).some((value) => value !== undefined), {
    message: 'At least one field must be updated',
  });

export const createAssessmentResponseSchema = z
  .object({
    capabilityId: z.string().min(1, 'Capability ID is required'),
    level: z
      .number()
      .int('Level must be a whole number')
      .min(1, 'Level must be at least 1')
      .max(6, 'Level must be at most 6'),
    comment: z.string().optional(),
  })
  .refine(
    (data) => {
      if (data.level <= 2) {
        return typeof data.comment === 'string' && data.comment.trim().length > 0;
      }
      return true;
    },
    {
      message: 'A comment is required for maturity levels 1 and 2',
      path: ['comment'],
    }
  );

export const createMaturityAssessmentSchema = z.object({
  frameworkId: z.string().min(1, 'Framework is required'),
  subjectEntityType: z.enum(['Service', 'Team']),
  subjectEntityId: z.string().min(1, 'Subject entity is required'),
  assessmentPeriod: z
    .string()
    .min(1, 'Assessment period is required')
    .regex(/^\d{4}-Q[1-4]$/, 'Period must be in format YYYY-QN (e.g. 2026-Q1)'),
  dueDate: z.string().min(1, 'Due date is required'),
  description: z.string().optional(),
  responses: z.array(createAssessmentResponseSchema).optional(),
});

export type CreateMaturityFrameworkFormData = z.infer<typeof createMaturityFrameworkSchema>;
export type UpdateMaturityFrameworkFormData = z.infer<typeof updateMaturityFrameworkSchema>;
export type AssessmentResponseFormData = z.infer<typeof createAssessmentResponseSchema>;
export type CreateMaturityAssessmentFormData = z.infer<typeof createMaturityAssessmentSchema>;
